import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import MainLayout from '../../components/layouts/MainLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';

import { CONTEXTS, CONTROLLERS, SERVICES } from 'figshelf-modules';

const GroupManagement = () => {
  const api = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);
  const groupController = useMemo(() => new CONTROLLERS.GroupController(api), [api]);

  const [search, setSearch] = useState('');
  const [visibility, setVisibility] = useState('');
  const [status, setStatus] = useState('');
  const [isVerified, setIsVerified] = useState('');
  const [isPremium, setIsPremium] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const resetToFirstPage = () => setPage(0);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: search.trim() || undefined,
        visibility: visibility || undefined,
        status: status || undefined,
      };

      if (isVerified !== '') params.isVerified = isVerified === 'true';
      if (isPremium !== '') params.isPremium = isPremium === 'true';

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'groups',
        params,
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 2 * 60 * 1000 });
      if (Array.isArray(cached?.groups)) {
        setGroups(cached.groups);
        setTotalItems(Number.isFinite(cached.totalItems) ? cached.totalItems : 0);
      }

      const res = await groupController.getGroupsAsAdmin(params);

      const payload = res?.data?.data;
      const rows = payload?.groups || [];
      const total = payload?.total || 0;

      setGroups(Array.isArray(rows) ? rows : []);
      setTotalItems(Number.isFinite(total) ? total : 0);

      await SERVICES.cacheAdminList(
        cacheKey,
        { groups: Array.isArray(rows) ? rows : [], totalItems: Number.isFinite(total) ? total : 0 },
        { scope: 'groups' },
      );
    } catch (e) {
      console.error('Failed to load groups:', e);
      setGroups([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [groupController, page, rowsPerPage, search, visibility, status, isVerified, isPremium]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const getStatusColor = (value) => {
    const v = String(value || '').toLowerCase();
    if (v === 'active') return 'success';
    if (v === 'pending') return 'warning';
    if (v === 'suspended') return 'error';
    if (v === 'inactive') return 'default';
    return 'default';
  };

  const getVisibilityColor = (value) => {
    const v = String(value || '').toLowerCase();
    if (v === 'public') return 'success';
    if (v === 'private') return 'warning';
    if (v === 'secret') return 'error';
    return 'default';
  };

  const handleOpenView = (g) => {
    setSelectedGroup(g);
    setViewDialogOpen(true);
  };

  return (
    <MainLayout>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>Group Management</Typography>
        <Typography variant="subtitle2" color="text.secondary" mb={3}>
          Browse and moderate user-created groups
        </Typography>

        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.3fr 200px 200px 200px 200px', gap: 2, mb: 2 }}>
              <TextField
                label="Search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); resetToFirstPage(); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />

              <FormControl size="small">
                <InputLabel>Visibility</InputLabel>
                <Select
                  label="Visibility"
                  value={visibility}
                  onChange={(e) => { setVisibility(e.target.value); resetToFirstPage(); }}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="Public">Public</MenuItem>
                  <MenuItem value="Private">Private</MenuItem>
                  <MenuItem value="Secret">Secret</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={status}
                  onChange={(e) => { setStatus(e.target.value); resetToFirstPage(); }}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small">
                <InputLabel>Verified</InputLabel>
                <Select
                  label="Verified"
                  value={isVerified}
                  onChange={(e) => { setIsVerified(e.target.value); resetToFirstPage(); }}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small">
                <InputLabel>Premium</InputLabel>
                <Select
                  label="Premium"
                  value={isPremium}
                  onChange={(e) => { setIsPremium(e.target.value); resetToFirstPage(); }}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Group</TableCell>
                    <TableCell>Visibility</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Verified</TableCell>
                    <TableCell>Premium</TableCell>
                    <TableCell>Members</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && groups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : groups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No groups found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    groups.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar src={g.currentAvatar || g.avatar || undefined} sx={{ width: 36, height: 36 }}>
                              <GroupIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{g.name || 'Untitled'}</Typography>
                              <Typography variant="caption" color="text.secondary">{g.slug || ''}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={g.visibility || '—'} size="small" color={getVisibilityColor(g.visibility)} />
                        </TableCell>
                        <TableCell>
                          <Chip label={g.status || '—'} size="small" color={getStatusColor(g.status)} />
                        </TableCell>
                        <TableCell>
                          <Chip label={g.isVerified ? 'Yes' : 'No'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={g.isPremium ? 'Yes' : 'No'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{g.memberCount ?? 0}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{g.owner?.username || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{g.createdAt ? new Date(g.createdAt).toLocaleDateString() : '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenView(g)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </CardContent>
        </Card>

        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Group Details</DialogTitle>
          <DialogContent>
            {selectedGroup && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body1" fontWeight={700}>{selectedGroup.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Slug</Typography>
                  <Typography variant="body1">{selectedGroup.slug || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Visibility</Typography>
                  <Typography variant="body1">{selectedGroup.visibility || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body1">{selectedGroup.status || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Join Type</Typography>
                  <Typography variant="body1">{selectedGroup.joinType || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Owner</Typography>
                  <Typography variant="body1">{selectedGroup.owner?.username || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Members</Typography>
                  <Typography variant="body1">{selectedGroup.memberCount ?? 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Followers</Typography>
                  <Typography variant="body1">{selectedGroup.followerCount ?? 0}</Typography>
                </Box>
                <Box style={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{selectedGroup.description || selectedGroup.shortDescription || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body2">{selectedGroup.metadata?.category || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography variant="body2">{selectedGroup.metadata?.location || '—'}</Typography>
                </Box>
                <Box style={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">Website</Typography>
                  <Typography variant="body2">{selectedGroup.metadata?.website || '—'}</Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default GroupManagement;
